// ============================================================
// InteractionSystem — Scribblenauts-style automatic reactions
//
// Every frame, checks proximity between all spawned entities
// and fires tag-pair rules automatically. No player input needed.
//
// Tags come from entity.config.special[] and entity.config.type
// ============================================================
import { sound } from './SoundEngine.js';

// Proximity radius for most interactions (px)
const NEAR = 140;
const TOUCH = 60;

export class InteractionSystem {
  /**
   * @param {import('../entities/SpawnedEntity.js').SpawnedEntity[]} entities
   * @param {import('../entities/TankCharacter.js').TankCharacter} player
   * @param {function(string, number)} onMessage  - show a HUD message
   */
  constructor(entities, player, onMessage) {
    this.entities  = entities;
    this.player    = player;
    this.onMessage = onMessage;

    // World bounds — updated by Game when a level loads
    this.worldBounds = { minX: 0, maxX: 4000 };

    // Cooldown table: "id_a:id_b:rule" → time remaining
    this._cooldowns = new Map();
    // Active states per entity
    this._states    = new Map();  // entityId → { scared, burning, following, lifted }
  }

  // ─────────────────────────────────────────────────────────
  // Main update — O(n²) but n is small (max 12 objects)
  // ─────────────────────────────────────────────────────────
  update(dt) {
    // Tick down cooldowns
    for (const [k, v] of this._cooldowns) {
      this._cooldowns.set(k, v - dt);
      if (v - dt <= 0) this._cooldowns.delete(k);
    }

    const ents  = this.entities;
    const n     = ents.length;

    for (let i = 0; i < n; i++) {
      const a = ents[i];
      if (!a.alive) continue;

      // ── Generalised floating-on-water rule ──────────────────
      // Any canFloat entity that is currently in water propels itself
      // gently in one direction, reversing when it hits a wall.
      // Vehicles are faster; other floaters drift slowly.
      this._applyFloatRule(a, dt);

      // ── Entity vs Player interactions ──
      this._checkVsPlayer(a, dt);

      // ── Entity vs Entity interactions ──
      for (let j = i + 1; j < n; j++) {
        const b = ents[j];
        if (!b.alive) continue;
        this._checkPair(a, b, dt);
      }

      // ── Ongoing state effects ──
      this._applyState(a, dt);
    }
  }

  // ─────────────────────────────────────────────────────────
  // Entity vs Player
  // ─────────────────────────────────────────────────────────
  _checkVsPlayer(ent, dt) {
    if (!this.player || this.player.dead) return;
    const dist = this._dist(ent.body, this.player.body);

    // FOOD heals player on touch
    if (ent.config.type === 'food' && ent.config.special?.includes('heal') && dist < TOUCH) {
      if (!this._cd(`${ent.id}:heal`)) {
        this._cd(`${ent.id}:heal`, 5);
        if (this.player.lives < 3) {
          this.player.lives = Math.min(3, this.player.lives + 1);
          this.onMessage?.(`🍕 Yum! +1 life!`);
          sound.collect();
        } else {
          this.onMessage?.(`🍕 Nom nom!`);
          sound.collect();
        }
        ent.alive = false; // consumed
      }
    }

    // BALLOON lifts player when nearby — same force as carry (tested: 1980)
    if (ent._isBalloon && dist < TOUCH) {
      this.player.body.ay -= 1980;
      this.player.body.vy *= 0.97;
      if (!this._cd(`${ent.id}:balloonpull`)) {
        this._cd(`${ent.id}:balloonpull`, 0.1);
        this.onMessage?.('🎈 Grab the balloon — it\'s lifting you!');
      }
    }

    // ROBOT follows player and pushes away hazards
    if (ent.config.ai === 'helper' && dist < 180) {
      ent._player = this.player;
    }

    // KEY unlocks nearby doors (future) — show hint
    if (ent.config.special?.includes('key') && dist < NEAR) {
      if (!this._cd(`${ent.id}:keyhint`)) {
        this._cd(`${ent.id}:keyhint`, 8);
        this.onMessage?.('🗝️ A key! Walk to a locked door to open it.');
      }
    }
  }

  // ─────────────────────────────────────────────────────────
  // Entity pair rules
  // ─────────────────────────────────────────────────────────
  _checkPair(a, b, dt) {
    const dist = this._dist(a.body, b.body);
    const cdKey = `${Math.min(a.id, b.id)}:${Math.max(a.id, b.id)}`;

    // ── FIRE interactions ─────────────────────────────────
    const fire = this._taggedWith(a, 'fire') ? a : this._taggedWith(b, 'fire') ? b : null;
    const other = fire === a ? b : a;
    if (fire && dist < NEAR) {

      // Fire + Creature → flee
      if (other.config.type === 'creature' && !this._getState(other, 'burning')) {
        this._setState(other, 'scared', true);
        this._setState(other, 'scaredOf', fire);
        if (!this._cd(`${cdKey}:scared`)) {
          this._cd(`${cdKey}:scared`, 4);
          this.onMessage?.(`🔥 ${other.config.emoji} is scared of the fire!`);
          sound.npcTalk();
        }
      }

      // Fire + Ice/Frozen → extinguish fire, melt ice
      if (this._taggedWith(other, 'slippery') || other.config.material === 'ice') {
        if (!this._cd(`${cdKey}:melt`) && dist < TOUCH) {
          this._cd(`${cdKey}:melt`, 3);
          this.onMessage?.('🧊 The ice melts the fire!');
          fire._destroyBoth = true;
          fire.alive = false;
          other.alive = false;
          sound.splash();
        }
      }

      // Fire + Food → burns it
      if (other.config.type === 'food' && dist < TOUCH) {
        if (!this._cd(`${cdKey}:burn`)) {
          this._cd(`${cdKey}:burn`, 2);
          this.onMessage?.(`🔥 The fire burns the ${other.config.emoji}!`);
          other.alive = false;
          sound._noise(0.2, { volume: 0.1, lowpass: 600 });
        }
      }
    }

    // ── BALLOON interactions ───────────────────────────────
    const balloon = this._taggedWith(a, 'lift') ? a : this._taggedWith(b, 'lift') ? b : null;
    const cargo   = balloon === a ? b : a;
    if (balloon && cargo !== balloon && dist < TOUCH) {
      // Balloon lifts any nearby object it touches
      if (!cargo.body.isStatic && cargo.config.type !== 'hazard') {
        cargo.body.ay  -= 2200;
        cargo.body.gravityScale = Math.max(0, (cargo.body.gravityScale ?? 1) - dt * 0.5);
        if (!this._cd(`${cdKey}:lift`)) {
          this._cd(`${cdKey}:lift`, 2);
          this.onMessage?.(`🎈 The balloon lifts the ${cargo.config.emoji}!`);
        }
      }
    }

    // ── MAGNET interactions ────────────────────────────────
    const magnet = this._taggedWith(a, 'attracts_metal') || this._taggedWith(a, 'magnetic')
      ? a : this._taggedWith(b, 'attracts_metal') || this._taggedWith(b, 'magnetic') ? b : null;
    const metal  = magnet === a ? b : a;
    if (magnet && metal !== magnet && dist < NEAR * 1.5) {
      const metalTypes = ['box', 'barrel', 'robot', 'key', 'gem'];
      const isMetallic = metalTypes.includes(metal.config.emoji?.replace(/\s/g,''))
        || metal.config.type === 'vehicle'
        || metal.config.type === 'item';
      if (isMetallic && !metal.body.isStatic) {
        // Pull toward magnet
        const dx = magnet.body.x - metal.body.x;
        const dy = magnet.body.y - metal.body.y;
        const d  = Math.sqrt(dx*dx + dy*dy) || 1;
        const force = 600 * (1 - d / (NEAR * 1.5));
        metal.body.ax += (dx / d) * force;
        metal.body.ay += (dy / d) * force;
        metal.body.wake();
        if (!this._cd(`${cdKey}:magnet`)) {
          this._cd(`${cdKey}:magnet`, 3);
          this.onMessage?.(`🧲 The magnet attracts the ${metal.config.emoji}!`);
        }
      }
    }

    // ── FOOD + Creature → follow ───────────────────────────
    const food     = a.config.type === 'food' ? a : b.config.type === 'food' ? b : null;
    const creature = food === a ? b : a;
    if (food && creature !== food && creature.config.type === 'creature' && dist < NEAR) {
      this._setState(creature, 'following', food);
      if (!this._cd(`${cdKey}:follow`)) {
        this._cd(`${cdKey}:follow`, 4);
        this.onMessage?.(`${creature.config.emoji} smells the ${food.config.emoji} and wants it!`);
        sound.npcTalk();
      }
    }

    // ── ROBOT + Creature → protect / interact ─────────────
    const robot    = a.config.ai === 'helper' ? a : b.config.ai === 'helper' ? b : null;
    const target   = robot === a ? b : a;
    if (robot && target !== robot && target.config.type === 'creature' && dist < NEAR) {
      if (!this._cd(`${cdKey}:robot_greet`)) {
        this._cd(`${cdKey}:robot_greet`, 6);
        this.onMessage?.(`🤖 The robot greets ${target.config.emoji}!`);
        sound.npcTalk();
      }
    }

    // ── BOMB near anything → arms it ──────────────────────
    const bomb = this._taggedWith(a, 'explosive') ? a : this._taggedWith(b, 'explosive') ? b : null;
    if (bomb && dist < TOUCH) {
      if (!this._cd(`${cdKey}:bombtick`)) {
        this._cd(`${cdKey}:bombtick`, 1.5);
        this.onMessage?.('💣 The bomb is about to explode!');
      }
    }

    // ── DRAGON + Creature → creature flees ────────────────
    const dragon   = a.config.emoji === '🐉' ? a : b.config.emoji === '🐉' ? b : null;
    const fleeing  = dragon === a ? b : a;
    if (dragon && fleeing !== dragon && fleeing.config.type === 'creature' && dist < NEAR * 1.5) {
      this._setState(fleeing, 'scared', true);
      this._setState(fleeing, 'scaredOf', dragon);
      if (!this._cd(`${cdKey}:dragonScare`)) {
        this._cd(`${cdKey}:dragonScare`, 5);
        this.onMessage?.(`🐉 The dragon scares ${fleeing.config.emoji}!`);
        sound.npcTalk();
      }
    }

    // ── TRAMPOLINE launches anything landing on it ─────────
    const trampoline = (this._taggedWith(a, 'bouncy') && a.config.type === 'platform') ? a
      : (this._taggedWith(b, 'bouncy') && b.config.type === 'platform') ? b : null;
    const bounced    = trampoline === a ? b : a;
    if (trampoline && bounced !== trampoline && dist < TOUCH) {
      if (bounced.body.onGround && bounced.body.vy >= -10) {
        bounced.body.vy = -700;
        bounced.body.wake();
        if (!this._cd(`${cdKey}:tramp`)) {
          this._cd(`${cdKey}:tramp`, 0.6);
          this.onMessage?.(`🤸 ${bounced.config.emoji || 'Object'} gets launched!`);
          sound.jump();
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────
  // Generalised float rule
  // Called every frame for every entity.
  // Rule: canFloat + inWater  →  self-propel at a constant speed.
  //   • Vehicles (boat, raft, helicopter): faster, narrative speed
  //   • Other floaters (barrel, box, ice): slow drift
  //   • Direction reverses on wall contact or every N seconds
  // The entity carries its own float-state in _states.
  // ─────────────────────────────────────────────────────────
  _applyFloatRule(ent, dt) {
    if (!ent.config.canFloat) return;
    if (!ent.body.inWater)    return;
    if (ent.body.isStatic)    return;

    const isVehicle = ent.config.type === 'vehicle';
    const speed     = isVehicle ? 90 : 30;

    const st = this._getOrCreateFloatState(ent);

    // Announce on first water entry
    if (!st.announced) {
      st.announced = true;
      const name = ent.config.emoji ?? ent.config.type;
      this.onMessage?.(`${name} drifts on the water!`);
    }

    const { minX, maxX } = this.worldBounds;
    const margin = ent.body.width / 2 + 40;   // turn before hitting the edge

    // ── Direction reversal triggers ────────────────────────
    // 1. World boundary — highest priority, hard bounce back
    if (ent.body.x - margin <= minX && st.dir < 0) {
      st.dir = 1;
      st.turnTimer = 4 + Math.random() * 4;
    } else if (ent.body.x + margin >= maxX && st.dir > 0) {
      st.dir = -1;
      st.turnTimer = 4 + Math.random() * 4;
    }

    // 2. Physics wall collision
    if (ent.body.onWall) {
      st.dir = -st.dir;
      st.turnTimer = 4 + Math.random() * 4;
      ent.body.onWall = false;
    }

    // 3. Periodic random turn (keeps it in the interesting area)
    st.turnTimer -= dt;
    if (st.turnTimer <= 0) {
      st.dir = -st.dir;
      st.turnTimer = 4 + Math.random() * 4;
    }

    ent.body.vx = st.dir * speed;
    ent.body.wake();
  }

  _getOrCreateFloatState(ent) {
    if (!this._states.has(ent.id)) this._states.set(ent.id, {});
    const st = this._states.get(ent.id);
    if (st.dir === undefined) {
      st.dir       = Math.random() < 0.5 ? 1 : -1;
      st.turnTimer = 4 + Math.random() * 4;
      st.announced = false;
    }
    return st;
  }

  // ─────────────────────────────────────────────────────────
  // Apply ongoing states (scared = run away, following = chase)
  // ─────────────────────────────────────────────────────────
  _applyState(ent, dt) {
    const st = this._states.get(ent.id);
    if (!st) return;

    // SCARED — run away from whatever scared it
    if (st.scared && st.scaredOf?.alive) {
      const scary = st.scaredOf;
      const dx = ent.body.x - scary.body.x;
      const dir = Math.sign(dx) || 1;
      if (ent.body.onGround) {
        ent.body.vx = dir * 160;
        ent.body.wake();
      }
      // Jump if blocked
      if (ent.body.onWall && ent.body.onGround) ent.body.vy = -450;
      // Forget after a while
      st._scaredTimer = (st._scaredTimer ?? 5) - dt;
      if (st._scaredTimer <= 0) { st.scared = false; st.scaredOf = null; }
    }

    // FOLLOWING food — move toward it
    if (st.following?.alive) {
      const food = st.following;
      const dx = food.body.x - ent.body.x;
      const dist = Math.abs(dx) + Math.abs(food.body.y - ent.body.y);
      if (dist < 30) {
        // Reached food — eat it
        food.alive = false;
        st.following = null;
        this.onMessage?.(`${ent.config.emoji} eats the food! 😋`);
        sound.collect();
      } else if (ent.body.onGround) {
        ent.body.vx = Math.sign(dx) * 120;
        ent.body.wake();
      }
    }
  }

  // ─────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────
  _dist(ba, bb) {
    const dx = ba.x - bb.x, dy = ba.y - bb.y;
    return Math.sqrt(dx*dx + dy*dy);
  }

  _taggedWith(ent, tag) {
    return ent.config?.special?.includes(tag);
  }

  _cd(key, set) {
    if (set !== undefined) { this._cooldowns.set(key, set); return; }
    return this._cooldowns.has(key) && this._cooldowns.get(key) > 0;
  }

  _getState(ent, key) {
    return this._states.get(ent.id)?.[key];
  }

  _setState(ent, key, val) {
    if (!this._states.has(ent.id)) this._states.set(ent.id, {});
    this._states.get(ent.id)[key] = val;
  }

  clear() {
    this._cooldowns.clear();
    this._states.clear();
  }
}
